import React, { useEffect, useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import {
  setIsBannedUser,
  setReplenishmentNotifications,
  toggleReplenishmentNotificationsModal
} from '../../actions';
import { companyNameQuery } from '../../api/queries';
import { CompanyNameQuery } from '../../graphql/graphql';
import {
  AuthEventType,
  clearSessionScopedApolloCache,
  subscribeToAuthEvents
} from '../../utils/authSync';
import { useBugsnagNotifier } from '../../hooks/useBugsnagNotifier';
import { AuthContext } from './context';
import { AuthProfile, AuthProviderProps } from './types';

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  isAuthInProgress = false,
  skipQuery = false,
  hasInitialProfile = false,
  initialProfile,
  initialLoading = false
}) => {
  const dispatch = useDispatch();
  const client = useApolloClient();

  const [localInitialProfile, setLocalInitialProfile] = useState<
    AuthProfile | null | undefined
  >(initialProfile);

  useEffect(() => {
    setLocalInitialProfile(initialProfile);
  }, [initialProfile]);

  const shouldSkipQueries = skipQuery || isAuthInProgress || hasInitialProfile;

  const {
    data,
    loading: companyNameLoading,
    error,
    refetch
  } = useQuery<CompanyNameQuery>(companyNameQuery, {
    ssr: false,
    skip: shouldSkipQueries,
    notifyOnNetworkStatusChange: true
  });

  useBugsnagNotifier(error);

  useEffect(() => {
    const unsubscribe = subscribeToAuthEvents((eventType: AuthEventType) => {
      if (eventType === 'LOGOUT') {
        setLocalInitialProfile(null);
      }

      clearSessionScopedApolloCache(client);
      window.location.reload();
    });

    return () => unsubscribe();
  }, [client]);

  const queriedProfile = data?.profile;
  const profile = hasInitialProfile
    ? (localInitialProfile ?? null)
    : ((queriedProfile as AuthProfile | null | undefined) ?? null);
  const loading = hasInitialProfile
    ? initialLoading
    : isAuthInProgress || companyNameLoading;

  useEffect(() => {
    if (profile?.isRestricted === undefined) {
      dispatch(setIsBannedUser(false));
      return;
    }

    dispatch(setIsBannedUser(profile.isRestricted));
  }, [profile, dispatch]);

  useEffect(() => {
    if (!profile?.businessNotifications?.length) {
      dispatch(setReplenishmentNotifications([]));
      dispatch(toggleReplenishmentNotificationsModal(false));
      return;
    }

    dispatch(setReplenishmentNotifications(profile.businessNotifications));
    dispatch(toggleReplenishmentNotificationsModal(true));
  }, [profile, dispatch]);

  const value = useMemo(
    () => ({
      profile,
      isAuthenticated: Boolean(profile),
      loading,
      error,
      refetchProfile: refetch
    }),
    [profile, loading, error, refetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
